import React from 'react'
import { useTranslation } from 'react-i18next'
import { ViewSectionProps } from './types'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataValue,
  ColoredBadge,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'

export const ViewSection: React.FC<ViewSectionProps> = ({ building }) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')

  const getLakeViewText = (value: string | number | undefined): string => {
    if (!value) return '-'

    const lakeViewMap: Record<string, string> = {
      '0 ha': t('buildings.filters.environmental.lakeViewArea.none'),
      'up to 100 ha': t('buildings.filters.environmental.lakeViewArea.small'),
      'more than 0 up to 100ha': t(
        'buildings.filters.environmental.lakeViewArea.small'
      ),
      'more than 100 ha': t(
        'buildings.filters.environmental.lakeViewArea.large'
      ),
      'more than 100ha': t(
        'buildings.filters.environmental.lakeViewArea.large'
      ),
      'more than 100m': t('buildings.filters.environmental.lakeViewArea.large'),
    }

    return lakeViewMap[value.toString().toLowerCase()] || value.toString()
  }

  const getMountainViewText = (value: string | number | undefined): string => {
    if (!value) return '-'

    const mountainViewMap: Record<string, string> = {
      '0 to 4 peaks': t(
        'buildings.filters.environmental.mountainViewQuality.few'
      ),
      '0 up to 4 peaks': t(
        'buildings.filters.environmental.mountainViewQuality.few'
      ),
      '5 to 13 peaks': t(
        'buildings.filters.environmental.mountainViewQuality.some'
      ),
      '5 up to 13 peaks': t(
        'buildings.filters.environmental.mountainViewQuality.some'
      ),
      'more than 13 peaks': t(
        'buildings.filters.environmental.mountainViewQuality.many'
      ),
    }

    return mountainViewMap[value.toString().toLowerCase()] || value.toString()
  }

  const getSlopeText = (slope: string | number | undefined): string => {
    if (!slope) return '-'

    const slopeMap: Record<string, string> = {
      '0 up to 4 degrees': t('common.slope.flat'),
      'more than 4 up to 9 degrees': t('common.slope.gentle'),
      'more than 9 degrees': t('common.slope.steep'),
    }

    return slopeMap[slope.toString().toLowerCase()] || slope.toString()
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.view.title')}
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          {building?.lake_view && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Vue lac')}
                explanation={tCommon('propertyExplanations.view.lakeView')}
              >
                {t('common.buildingTooltip.Vue lac')}
              </DataLabelWithTooltip>
              <ColoredBadge $type="lake_view" $value={building.lake_view}>
                {getLakeViewText(building.lake_view)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.mountain_view && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Vue montagne')}
                explanation={tCommon('propertyExplanations.view.mountainView')}
              >
                {t('common.buildingTooltip.Vue montagne')}
              </DataLabelWithTooltip>
              <ColoredBadge
                $type="mountain_view"
                $value={building.mountain_view}
              >
                {getMountainViewText(building.mountain_view)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.slope && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Pente')}
                explanation={tCommon('propertyExplanations.view.slope')}
              >
                {t('common.buildingTooltip.Pente')}
              </DataLabelWithTooltip>
              <ColoredBadge $type="slope" $value={building.slope}>
                {getSlopeText(building.slope)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.altitude_max_ref && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Altitude max')}
                explanation={tCommon('propertyExplanations.view.maxAltitude')}
              >
                {t('common.buildingTooltip.Altitude max')}
              </DataLabelWithTooltip>
              <DataValue>
                {typeof building.altitude_max_ref === 'number'
                  ? `${building.altitude_max_ref.toLocaleString()} m`
                  : building.altitude_max_ref}
              </DataValue>
            </DataRow>
          )}

          {building?.altitude_min_ref && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Altitude min')}
                explanation={tCommon('propertyExplanations.view.minAltitude')}
              >
                {t('common.buildingTooltip.Altitude min')}
              </DataLabelWithTooltip>
              <DataValue>
                {typeof building.altitude_min_ref === 'number'
                  ? `${building.altitude_min_ref.toLocaleString()} m`
                  : building.altitude_min_ref}
              </DataValue>
            </DataRow>
          )}
        </DataGrid>
      </SectionContent>
    </InfoSection>
  )
}

export default ViewSection
